import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Text,
  RadioGroup,
  Radio,
  Stack,
} from '@chakra-ui/react';
import { User, Rock, Issue, Metric } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';

interface L10AddActionItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: any) => Promise<void>;
  users?: User[]; // Optional - component now uses eosUsers from AuthContext
  rocks: Rock[];
  issues: Issue[];
  metrics: Metric[];
  currentUserId?: string;
}

/**
 * L10 Add Action Item Modal Component
 * Uses Formik for form state and Yup for validation
 * Migrated from Tailwind to Chakra UI per Directive 5
 */
export function L10AddActionItemModal({
  isOpen,
  onClose,
  onSave,
  users,
  rocks,
  issues,
  metrics,
  currentUserId,
}: L10AddActionItemModalProps) {
  const { eosUsers = [] } = useAuth();
  // Define Validation Schema with Yup
  const ActionItemSchema = Yup.object().shape({
    description: Yup.string()
      .min(10, 'Description must be at least 10 characters')
      .max(500, 'Description must be 500 characters or less')
      .required('Description is required'),
    ownerId: Yup.string().required('An owner must be assigned'),
    dueDate: Yup.date().required('Due date is required'),
    linkType: Yup.string(),
    linkedRockId: Yup.string().when('linkType', {
      is: 'rock',
      then: (schema) => schema.required('A rock must be selected'),
    }),
    linkedIssueId: Yup.string().when('linkType', {
      is: 'issue',
      then: (schema) => schema.required('An issue must be selected'),
    }),
    linkedMetricId: Yup.string().when('linkType', {
      is: 'metric',
      then: (schema) => schema.required('A metric must be selected'),
    }),
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    // Translate Formik values to the data model
    const actionItemData = {
      description: values.description,
      ownerId: values.ownerId,
      dueDate: values.dueDate,
      linkedRockId: values.linkType === 'rock' ? values.linkedRockId : null,
      linkedIssueId: values.linkType === 'issue' ? values.linkedIssueId : null,
      linkedMetricId: values.linkType === 'metric' ? values.linkedMetricId : null,
      assignedToUserId: values.assignedToUserId || null,
    };
    await onSave(actionItemData);
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Action Item</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={{
            description: '',
            ownerId: '',
            assignedToUserId: '',
            dueDate: '',
            linkType: 'none',
            linkedRockId: '',
            linkedIssueId: '',
            linkedMetricId: '',
          }}
          validationSchema={ActionItemSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, errors, touched, setFieldValue }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4}>
                  {/* Description Field */}
                  <Field name="description">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={form.errors.description && form.touched.description}
                        w="full"
                      >
                        <FormLabel>Description</FormLabel>
                        <Textarea {...field} placeholder="Enter description..." rows={3} />
                        <HStack justify="space-between">
                          <FormErrorMessage>{form.errors.description}</FormErrorMessage>
                          <Text fontSize="xs" color="gray.500">
                            {values.description.length}/500
                          </Text>
                        </HStack>
                      </FormControl>
                    )}
                  </Field>

                  {/* Owner Field */}
                  <Field name="ownerId">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={form.errors.ownerId && form.touched.ownerId}
                        w="full"
                      >
                        <FormLabel>Owner</FormLabel>
                        <Select {...field} placeholder="Select owner">
                          {eosUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{form.errors.ownerId}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Assigned To Field (Optional) */}
                  <Field name="assignedToUserId">
                    {({ field }: any) => (
                      <FormControl w="full">
                        <FormLabel>Assigned To (Optional)</FormLabel>
                        <Select {...field} placeholder="Select assignee">
                          <option value="">None</option>
                          {eosUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>

                  {/* Due Date Field */}
                  <Field name="dueDate">
                    {({ field, form }: any) => (
                      <FormControl
                        isInvalid={form.errors.dueDate && form.touched.dueDate}
                        w="full"
                      >
                        <FormLabel>Due Date</FormLabel>
                        <Input {...field} type="date" />
                        <FormErrorMessage>{form.errors.dueDate}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Link Type Radio Group */}
                  <FormControl w="full">
                    <FormLabel>Link To (Optional)</FormLabel>
                    <RadioGroup
                      value={values.linkType}
                      onChange={(val) => setFieldValue('linkType', val)}
                    >
                      <Stack direction="row" spacing={4}>
                        <Radio value="none">None</Radio>
                        <Radio value="rock">Rock</Radio>
                        <Radio value="issue">Issue</Radio>
                        <Radio value="metric">Metric</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  {/* Conditional Linked Rock Select */}
                  {values.linkType === 'rock' && (
                    <Field name="linkedRockId">
                      {({ field, form }: any) => (
                        <FormControl
                          isInvalid={form.errors.linkedRockId && form.touched.linkedRockId}
                          w="full"
                        >
                          <FormLabel>Select Rock</FormLabel>
                          <Select {...field} placeholder="-- Select Rock --">
                            {rocks.map((rock) => (
                              <option key={rock.id} value={rock.id}>
                                {rock.rock}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{form.errors.linkedRockId}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {/* Conditional Linked Issue Select */}
                  {values.linkType === 'issue' && (
                    <Field name="linkedIssueId">
                      {({ field, form }: any) => (
                        <FormControl
                          isInvalid={form.errors.linkedIssueId && form.touched.linkedIssueId}
                          w="full"
                        >
                          <FormLabel>Select Issue</FormLabel>
                          <Select {...field} placeholder="-- Select Issue --">
                            {issues.map((issue) => (
                              <option key={issue.id} value={issue.id}>
                                {issue.name}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{form.errors.linkedIssueId}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {/* Conditional Linked Metric Select */}
                  {values.linkType === 'metric' && (
                    <Field name="linkedMetricId">
                      {({ field, form }: any) => (
                        <FormControl
                          isInvalid={form.errors.linkedMetricId && form.touched.linkedMetricId}
                          w="full"
                        >
                          <FormLabel>Select Metric</FormLabel>
                          <Select {...field} placeholder="-- Select Metric --">
                            {metrics.map((metric) => (
                              <option key={metric.id} value={metric.id}>
                                {metric.metricName || 'Unknown Metric'}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{form.errors.linkedMetricId}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onClose} mr={3}>
                  Cancel
                </Button>
                <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
                  Save Action Item
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
